import "./GuestMaster.css";

import { useState, useEffect, useCallback } from "react";
import axios from "axios";

import editIcon from "../../assets/icons/edit.png";
import BackIcon from "../../assets/icons/arrow.png";

import { useAuthStore } from "../../Services/authStore";
import { toastSuccess, toastError, toastWarning } from "../../Services/toaster";

import Select from "react-select";

// ─────────────────────────────────────────────────────────
// GUEST MASTER COMPONENT
// ─────────────────────────────────────────────────────────

export default function GuestMaster() {
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [selectedGuest, setSelectedGuest] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [eventOptions, setEventOptions] = useState([]);
  const [eventLoading, setEventLoading] = useState(false);

  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedEventFilter, setSelectedEventFilter] = useState(null);
  const [selectedGuestFilter, setSelectedGuestFilter] = useState(null);

  const token = useAuthStore((state) => state.token);

  const API_URL = process.env.REACT_APP_API_URL;

  // ════════════════════════════════════════════════════
  // DROPDOWN OPTIONS
  // ════════════════════════════════════════════════════

  const titleOptions = [
    { value: "Mr.", label: "Mr." },
    { value: "Ms.", label: "Ms." },
    { value: "Mrs.", label: "Mrs." },
    { value: "Dr.", label: "Dr." },
    { value: "Prof.", label: "Prof." },
    { value: "Rev.", label: "Rev." },
    { value: "Hon.", label: "Hon." },
  ];

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];

  const countryOptions = [
    { value: "United States", label: "United States" },
    { value: "United Kingdom", label: "United Kingdom" },
    { value: "Canada", label: "Canada" },
    { value: "Mexico", label: "Mexico" },
    { value: "Germany", label: "Germany" },
    { value: "Spain", label: "Spain" },
    { value: "Australia", label: "Australia" },
    { value: "France", label: "France" },
    { value: "Italy", label: "Italy" },
    { value: "India", label: "India" },
  ];

  const stateOptions = {
    "United States": ["California", "New York", "Texas", "Florida", "Illinois"],

    "United Kingdom": ["London", "Manchester", "Birmingham", "Edinburgh"],

    Canada: ["Ontario", "Quebec", "British Columbia", "Alberta"],

    Mexico: ["Jalisco", "Mexico City", "Nuevo Leon", "Veracruz"],

    Germany: ["Bavaria", "Berlin", "Hamburg", "Hesse"],

    Spain: ["Catalonia", "Madrid", "Andalusia", "Valencia"],

    Australia: [
      "New South Wales",
      "Victoria",
      "Queensland",
      "Western Australia",
    ],

    France: ["Île-de-France", "Provence", "Normandy", "Bordeaux"],

    Italy: ["Lombardy", "Lazio", "Tuscany", "Veneto"],

    India: ["Maharashtra", "Delhi", "Karnataka", "Tamil Nadu"],
  };

  const languageOptions = [
    { value: "Spanish", label: "Spanish" },
    { value: "French", label: "French" },
    { value: "German", label: "German" },
    { value: "Italian", label: "Italian" },
    { value: "Portuguese", label: "Portuguese" },
    { value: "Chinese", label: "Chinese" },
    { value: "Japanese", label: "Japanese" },
    { value: "Korean", label: "Korean" },
    { value: "Arabic", label: "Arabic" },
    { value: "Russian", label: "Russian" },
    { value: "Hindi", label: "Hindi" },
    { value: "Marathi", label: "Marathi" },
    { value: "Tamil", label: "Tamil" },
    { value: "Telugu", label: "Telugu" },
    { value: "Bengali", label: "Bengali" },
  ];

  const dietPrefOptions = [
    { value: "No restrictions", label: "No restrictions" },
    { value: "Vegetarian", label: "Vegetarian" },
    { value: "Vegan", label: "Vegan" },
    { value: "Gluten-free", label: "Gluten-free" },
    { value: "Halal", label: "Halal" },
    { value: "Kosher", label: "Kosher" },
    { value: "Pescatarian", label: "Pescatarian" },
    { value: "No pork", label: "No pork" },
    { value: "No dairy", label: "No dairy" },
    { value: "No seafood", label: "No seafood" },
    { value: "Organic only", label: "Organic only" },
    { value: "Diabetic", label: "Diabetic" },
    { value: "Low sodium", label: "Low sodium" },
  ];

  // ════════════════════════════════════════════════════
  // INITIAL FORM
  // ════════════════════════════════════════════════════

  const emptyFormData = {
    first_name: "",
    last_name: "",
    birth_date: "",
    title: "",
    gender: "",
    nationality: "",
    additional_spoken_language: "",
    phone: "",
    alternate_phone: "",
    email: "",
    amount: "",
    currency: "INR",
    address: "",
    add_information: "",
    country: "",
    state: "",
    postal_code: "",
    city: "",
    diet_pref: "",
    id_type: "",
    id_number: "",
    is_active: 1,
    event_id: "",
  };

  const [formData, setFormData] = useState(emptyFormData);

  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  // ════════════════════════════════════════════════════
  // GET EVENTS
  // GET /events/list
  // ════════════════════════════════════════════════════

  const getEvents = useCallback(async () => {
    try {
      setEventLoading(true);

      const response = await axios.get(`${API_URL}/events/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      console.log("Events API:", response.data);

      const events = response.data?.data || [];

      const options = events.map((event) => ({
        value: event.id,
        label: event.event_title,
      }));

      setEventOptions(options);

      return options;
    } catch (err) {
      console.error("Failed to load events:", err.response?.data || err);

      toastError("Failed to load events");

      setEventOptions([]);

      return [];
    } finally {
      setEventLoading(false);
    }
  }, [API_URL, token]);

  // ════════════════════════════════════════════════════
  // MAP GUEST DATA
  // ════════════════════════════════════════════════════

  const mapGuest = (item, eventIdFromApi = null) => {
    return {
      id: item.id || item.guest_id,

      first_name: item.first_name || "",
      last_name: item.last_name || "",

      birth_date: item.birth_date || "",

      title: item.title || "",
      gender: item.gender || "",
      nationality: item.nationality || "",

      additional_spoken_language: item.additional_spoken_language || "",

      phone: item.phone || "",
      alternate_phone: item.alternate_phone || "",

      email: item.email || "",

      amount: item.amount ?? 0,
      currency: item.currency || "INR",

      address: item.address || "",
      add_information: item.add_information || "",

      country: item.country || "",
      state: item.state || "",
      postal_code: item.postal_code || "",
      city: item.city || "",

      diet_pref: item.diet_pref || "",

      id_type: item.id_type || "",
      id_number: item.id_number || "",

      is_active: item.is_active === true || item.is_active === 1,

      // IMPORTANT:
      // If API response doesn't contain event_id,
      // use the event ID from the request.
      event_id: item.event_id ?? item.eventId ?? eventIdFromApi,
    };
  };

  // ════════════════════════════════════════════════════
  // GET GUESTS FOR ONE EVENT
  // GET /guests/event/{event_id}
  // ════════════════════════════════════════════════════

  const getGuestsByEvent = useCallback(
    async (eventId) => {
      try {
        const response = await axios.get(`${API_URL}/guests/event/${eventId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });

        console.log(`Guests API for event ${eventId}:`, response.data);

        const data = response.data?.data || response.data || [];

        if (!Array.isArray(data)) {
          return [];
        }

        return data.map((item) => mapGuest(item, eventId));
      } catch (err) {
        console.error(
          `Failed to load guests for event ${eventId}:`,
          err.response?.data || err,
        );

        return [];
      }
    },
    [API_URL, token],
  );

  // ════════════════════════════════════════════════════
  // GET ALL GUESTS
  //
  // Backend doesn't provide GET /guests.
  //
  // Therefore:
  // 1. Get all events
  // 2. Call /guests/event/{id} for every event
  // 3. Combine the results
  // ════════════════════════════════════════════════════

  const getGuests = useCallback(
    async (events = null) => {
      try {
        setLoading(true);

        let availableEvents = events;

        if (!availableEvents || availableEvents.length === 0) {
          availableEvents = await getEvents();
        }

        if (!availableEvents || availableEvents.length === 0) {
          setGuests([]);
          return;
        }

        console.log("Loading guests for events:", availableEvents);

        const results = await Promise.all(
          availableEvents.map((event) => getGuestsByEvent(event.value)),
        );

        const allGuests = results.flat();

        // Remove duplicate guests if backend returns duplicates
        const uniqueGuests = Array.from(
          new Map(allGuests.map((guest) => [String(guest.id), guest])).values(),
        );

        console.log("All guests:", uniqueGuests);

        setGuests(uniqueGuests);
      } catch (err) {
        console.error("Failed to load guests:", err);

        toastError("Failed to load guests");

        setGuests([]);
      } finally {
        setLoading(false);
      }
    },
    [getEvents, getGuestsByEvent],
  );

  // ════════════════════════════════════════════════════
  // INITIAL LOAD
  // ════════════════════════════════════════════════════

  useEffect(() => {
    const loadInitialData = async () => {
      const events = await getEvents();

      if (events.length > 0) {
        await getGuests(events);
      } else {
        setGuests([]);
      }
    };

    loadInitialData();
  }, [getEvents, getGuests]);

  // ════════════════════════════════════════════════════
  // GET SINGLE GUEST
  // GET /guests/{guest_id}
  // ════════════════════════════════════════════════════

  const getGuestById = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${API_URL}/guests/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });

        const data = response.data?.data || response.data;

        return mapGuest(data);
      } catch (err) {
        console.error("Failed to fetch guest:", err.response?.data || err);

        throw err;
      }
    },
    [API_URL, token],
  );

  // ════════════════════════════════════════════════════
  // CREATE GUEST
  // POST /guests
  // ════════════════════════════════════════════════════

  const createGuest = async (guestData) => {
    try {
      const payload = {
        first_name: guestData.first_name,
        last_name: guestData.last_name,
        birth_date: guestData.birth_date,

        title: guestData.title,
        gender: guestData.gender,
        nationality: guestData.nationality,

        additional_spoken_language: guestData.additional_spoken_language,

        phone: guestData.phone,
        alternate_phone: guestData.alternate_phone,
        email: guestData.email,

        amount: guestData.amount === "" ? 0 : parseFloat(guestData.amount),

        currency: guestData.currency || "INR",

        address: guestData.address,
        add_information: guestData.add_information,

        country: guestData.country,
        state: guestData.state,
        postal_code: guestData.postal_code,
        city: guestData.city,

        diet_pref: guestData.diet_pref,

        id_type: guestData.id_type,
        id_number: guestData.id_number,

        is_active: guestData.is_active === 1,

        event_id: parseInt(guestData.event_id, 10),
      };

      console.log("Create Guest Payload:", payload);

      const response = await axios.post(`${API_URL}/guests`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      return response.data;
    } catch (err) {
      console.error("Create guest error:", err.response?.data || err);

      throw err;
    }
  };

  // ════════════════════════════════════════════════════
  // UPDATE GUEST
  // PUT /guests/{guest_id}
  // ════════════════════════════════════════════════════

  const updateGuest = async (id, guestData) => {
    try {
      const payload = {
        first_name: guestData.first_name,
        last_name: guestData.last_name,
        birth_date: guestData.birth_date,

        title: guestData.title,
        gender: guestData.gender,
        nationality: guestData.nationality,

        additional_spoken_language: guestData.additional_spoken_language,

        phone: guestData.phone,
        alternate_phone: guestData.alternate_phone,
        email: guestData.email,

        amount: guestData.amount === "" ? 0 : parseFloat(guestData.amount),

        currency: guestData.currency || "INR",

        address: guestData.address,
        add_information: guestData.add_information,

        country: guestData.country,
        state: guestData.state,
        postal_code: guestData.postal_code,
        city: guestData.city,

        diet_pref: guestData.diet_pref,

        id_type: guestData.id_type,

        id_number: guestData.id_number,

        is_active: guestData.is_active === 1,

        event_id: parseInt(guestData.event_id, 10),
      };

      console.log("Update Guest Payload:", payload);

      const response = await axios.put(`${API_URL}/guests/${id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      return response.data;
    } catch (err) {
      console.error("Update guest error:", err.response?.data || err);

      throw err;
    }
  };

  // ════════════════════════════════════════════════════
  // DELETE GUEST
  // DELETE /guests/{guest_id}
  // ════════════════════════════════════════════════════

  const deleteGuest = async (id) => {
    try {
      const response = await axios.delete(`${API_URL}/guests/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      return response.data;
    } catch (err) {
      console.error("Delete guest error:", err.response?.data || err);

      throw err;
    }
  };

  // ════════════════════════════════════════════════════
  // OPEN EDIT
  // ════════════════════════════════════════════════════

  const openEdit = async (guest) => {
    setFormError("");

    setIsEditMode(true);
    setSelectedGuest(guest);
    setEditingId(guest.id);

    setFormLoading(true);
    setShowForm(true);

    try {
      const guestData = await getGuestById(guest.id);

      setFormData({
        first_name: guestData.first_name || "",

        last_name: guestData.last_name || "",

        birth_date: guestData.birth_date || "",

        title: guestData.title || "",

        gender: guestData.gender || "",

        nationality: guestData.nationality || "",

        additional_spoken_language: guestData.additional_spoken_language || "",

        phone: guestData.phone || "",

        alternate_phone: guestData.alternate_phone || "",

        email: guestData.email || "",

        amount: guestData.amount ?? "",

        currency: guestData.currency || "INR",

        address: guestData.address || "",

        add_information: guestData.add_information || "",

        country: guestData.country || "",

        state: guestData.state || "",

        postal_code: guestData.postal_code || "",

        city: guestData.city || "",

        diet_pref: guestData.diet_pref || "",

        id_type: guestData.id_type || "",

        id_number: guestData.id_number || "",

        is_active: guestData.is_active ? 1 : 0,

        event_id: guestData.event_id ?? guest.event_id ?? "",
      });
    } catch (err) {
      const msg = "Failed to load guest details.";

      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  // ════════════════════════════════════════════════════
  // OPEN ADD
  // ════════════════════════════════════════════════════

  const openAdd = () => {
    setIsEditMode(false);
    setSelectedGuest(null);
    setEditingId(null);

    setFormData({
      ...emptyFormData,
      event_id: "",
    });

    setFormError("");
    setShowForm(true);
  };

  // ════════════════════════════════════════════════════
  // CLOSE FORM
  // ════════════════════════════════════════════════════

  const closeForm = () => {
    setShowForm(false);

    setFormData({
      ...emptyFormData,
    });

    setFormError("");

    setSelectedGuest(null);
    setEditingId(null);

    setIsEditMode(false);
  };

  // ════════════════════════════════════════════════════
  // INPUT CHANGE
  // ════════════════════════════════════════════════════

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  };

  // ════════════════════════════════════════════════════
  // COUNTRY CHANGE
  // ════════════════════════════════════════════════════

  const handleCountryChange = (e) => {
    const country = e.target.value;

    setFormData((prev) => ({
      ...prev,
      country,
      state: "",
    }));
  };

  // ════════════════════════════════════════════════════
  // SUBMIT
  // ════════════════════════════════════════════════════

  const handleSubmit = async () => {
    setFormError("");

    if (!formData.event_id) {
      const msg = "Please select an event.";

      setFormError(msg);
      toastWarning(msg);
      return;
    }

    if (!formData.first_name.trim()) {
      const msg = "First name is required.";

      setFormError(msg);
      toastWarning(msg);
      return;
    }

    if (!formData.last_name.trim()) {
      const msg = "Last name is required.";

      setFormError(msg);
      toastWarning(msg);
      return;
    }

    if (!formData.email.trim()) {
      const msg = "Email is required.";

      setFormError(msg);
      toastWarning(msg);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.email)) {
      const msg = "Please enter a valid email address.";

      setFormError(msg);
      toastWarning(msg);
      return;
    }

    if (!formData.phone.trim()) {
      const msg = "Phone number is required.";

      setFormError(msg);
      toastWarning(msg);
      return;
    }

    if (!formData.birth_date) {
      const msg = "Birth date is required.";

      setFormError(msg);
      toastWarning(msg);
      return;
    }

    setFormLoading(true);

    try {
      if (isEditMode) {
        await updateGuest(editingId, formData);

        toastSuccess("Guest updated successfully.");
      } else {
        await createGuest(formData);

        toastSuccess("Guest created successfully.");
      }

      closeForm();

      // Reload all guests from all events
      await getGuests();
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message;

      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to save guest.";

      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  // ════════════════════════════════════════════════════
  // TOGGLE STATUS
  // ════════════════════════════════════════════════════

  const toggleGuestStatus = async (guest) => {
    const newStatus = guest.is_active ? 0 : 1;

    try {
      await updateGuest(guest.id, {
        ...guest,
        is_active: newStatus,
      });

      setGuests((prev) =>
        prev.map((g) =>
          g.id === guest.id
            ? {
                ...g,
                is_active: newStatus === 1,
              }
            : g,
        ),
      );

      toastSuccess(
        newStatus === 1
          ? "Guest activated successfully."
          : "Guest deactivated successfully.",
      );
    } catch (err) {
      const detail = err.response?.data?.detail;

      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to update guest status.";

      toastError(msg);
    }
  };

  // ════════════════════════════════════════════════════
  // DELETE
  // ════════════════════════════════════════════════════

  const handleDelete = async (guest) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${guest.first_name} ${guest.last_name}"?`,
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteGuest(guest.id);

      setGuests((prev) => prev.filter((g) => g.id !== guest.id));

      toastSuccess("Guest deleted successfully.");
    } catch (err) {
      const detail = err.response?.data?.detail;

      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to delete guest.";

      toastError(msg);
    }
  };

  // ════════════════════════════════════════════════════
  // GUEST DROPDOWN OPTIONS
  // ════════════════════════════════════════════════════

  const guestOptions = guests.map((guest) => ({
    value: guest.id,

    label: `${guest.first_name} ${guest.last_name}`,

    event_id: guest.event_id,
  }));

  // ════════════════════════════════════════════════════
  // FILTER GUESTS
  // ════════════════════════════════════════════════════

  const filteredGuests = guests.filter((guest) => {
    const search = searchTerm.toLowerCase().trim();

    const fullName = `${guest.first_name || ""} ${
      guest.last_name || ""
    }`.toLowerCase();

    const matchesSearch =
      !search ||
      fullName.includes(search) ||
      guest.email?.toLowerCase().includes(search) ||
      guest.phone?.toLowerCase().includes(search);

    const matchesEvent =
      !selectedEventFilter ||
      String(guest.event_id) === String(selectedEventFilter.value);

    const matchesGuest =
      !selectedGuestFilter ||
      String(guest.id) === String(selectedGuestFilter.value);

    return matchesSearch && matchesEvent && matchesGuest;
  });

  // ════════════════════════════════════════════════════
  // FORMAT CURRENCY
  // ════════════════════════════════════════════════════

  const formatCurrency = (amount, currency = "INR") => {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currency} ${amount || 0}`;
    }
  };

  // ════════════════════════════════════════════════════
  // FORMAT DATE
  // ════════════════════════════════════════════════════

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // ════════════════════════════════════════════════════
  // FORM PAGE
  // ════════════════════════════════════════════════════

  if (showForm) {
    return (
      <div className="gm-guest-form-page">
        <div className="gm-guest-form-header">
          <div className="gm-guest-form-header-left">
            <button className="gm-guest-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="gm-guest-back-icon" />
            </button>

            <div>
              <h2 className="gm-guest-form-title">
                {isEditMode ? "Edit Guest" : "Add Guest"}
              </h2>

              <p className="gm-guest-form-subtitle">
                {isEditMode
                  ? "Update guest details and status"
                  : "Create a new guest profile"}
              </p>
            </div>
          </div>
        </div>

        <div className="gm-guest-form-card">
          {formLoading && (
            <p
              style={{
                color: "#9F8E87",
                fontSize: 13,
                marginBottom: 14,
              }}
            >
              {isEditMode ? "Loading..." : "Saving..."}
            </p>
          )}

          {formError && (
            <p
              style={{
                color: "#b94040",
                fontSize: 13,
                marginBottom: 14,
                whiteSpace: "pre-line",
              }}
            >
              {formError}
            </p>
          )}

          <div className="gm-guest-form-grid">
            {/* PERSONAL INFORMATION */}

            <div className="gm-guest-section-title gm-col-full">
              <h3>Personal Information</h3>
            </div>

            {/* EVENT */}

            <div className="gm-guest-form-group">
              <label>Event *</label>

              <Select
                name="event_id"
                options={eventOptions}
                isSearchable
                isClearable
                isLoading={eventLoading}
                value={
                  formData.event_id
                    ? eventOptions.find(
                        (option) =>
                          String(option.value) === String(formData.event_id),
                      ) || null
                    : null
                }
                onChange={(selected) => {
                  setFormData((prev) => ({
                    ...prev,
                    event_id: selected ? selected.value : "",
                  }));
                }}
                placeholder="Select Event"
                classNamePrefix="gm-event-select"
              />
            </div>

            {/* TITLE */}

            <div className="gm-guest-form-group">
              <label>Title</label>

              <select
                name="title"
                value={formData.title}
                onChange={handleChange}
              >
                <option value="">Select Title</option>

                {titleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* FIRST NAME */}

            <div className="gm-guest-form-group">
              <label>First Name *</label>

              <input
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="Enter first name"
              />
            </div>

            {/* LAST NAME */}

            <div className="gm-guest-form-group">
              <label>Last Name *</label>

              <input
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Enter last name"
              />
            </div>

            {/* BIRTH DATE */}

            <div className="gm-guest-form-group">
              <label>Birth Date *</label>

              <input
                name="birth_date"
                type="date"
                value={formData.birth_date}
                onChange={handleChange}
              />
            </div>

            {/* GENDER */}

            <div className="gm-guest-form-group">
              <label>Gender</label>

              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
              >
                <option value="">Select Gender</option>

                {genderOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* NATIONALITY */}

            <div className="gm-guest-form-group">
              <label>Nationality</label>

              <input
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
                placeholder="Enter nationality"
              />
            </div>

            {/* LANGUAGES */}

            <div className="gm-guest-form-group">
              <label>Additional Spoken Languages</label>

              <Select
                isMulti
                isSearchable
                options={languageOptions}
                value={
                  formData.additional_spoken_language
                    ? formData.additional_spoken_language
                        .split(",")
                        .map((language) => language.trim())
                        .filter(Boolean)
                        .map((language) => ({
                          value: language,
                          label: language,
                        }))
                    : []
                }
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,

                    additional_spoken_language: selected
                      ? selected.map((item) => item.value).join(", ")
                      : "",
                  }))
                }
                placeholder="Search and select languages..."
                classNamePrefix="gm-language-select"
                closeMenuOnSelect={false}
              />
            </div>

            {/* CONTACT */}

            <div className="gm-guest-section-title gm-col-full">
              <h3>Contact Information</h3>
            </div>

            <div className="gm-guest-form-group">
              <label>Phone *</label>

              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1-555-0101"
              />
            </div>

            <div className="gm-guest-form-group">
              <label>Alternate Phone</label>

              <input
                name="alternate_phone"
                value={formData.alternate_phone}
                onChange={handleChange}
                placeholder="Alternate phone number"
              />
            </div>

            <div className="gm-guest-form-group">
              <label>Email *</label>

              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="guest@example.com"
              />
            </div>

            <div className="gm-guest-form-group">
              <label>Amount</label>

              <input
                name="amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00"
              />
            </div>

            <div className="gm-guest-form-group">
              <label>Currency</label>

              <select
                name="currency"
                value={formData.currency}
                onChange={handleChange}
              >
                <option value="INR">INR</option>

                <option value="USD">USD</option>

                <option value="EUR">EUR</option>

                <option value="GBP">GBP</option>
              </select>
            </div>

            {/* ADDRESS */}

            <div className="gm-guest-section-title gm-col-full">
              <h3>Address Information</h3>
            </div>

            <div className="gm-guest-form-group gm-col-full">
              <label>Address</label>

              <input
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Street address"
              />
            </div>

            <div className="gm-guest-form-group">
              <label>Country</label>

              <select
                name="country"
                value={formData.country}
                onChange={handleCountryChange}
              >
                <option value="">Select Country</option>

                {countryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="gm-guest-form-group">
              <label>State</label>

              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                disabled={!formData.country}
              >
                <option value="">Select State</option>

                {formData.country &&
                  stateOptions[formData.country]?.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
              </select>
            </div>

            <div className="gm-guest-form-group">
              <label>City</label>

              <input
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Enter city"
              />
            </div>

            <div className="gm-guest-form-group">
              <label>Postal Code</label>

              <input
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                placeholder="Enter postal code"
              />
            </div>

            {/* ADDITIONAL */}

            <div className="gm-guest-section-title gm-col-full">
              <h3>Additional Information</h3>
            </div>

            <div className="gm-guest-form-group gm-col-full">
              <label>Additional Information</label>

              <textarea
                name="add_information"
                rows="3"
                value={formData.add_information}
                onChange={handleChange}
                placeholder="Any additional notes or special requirements..."
              />
            </div>

            {/* DIET */}

            <div className="gm-guest-form-group gm-col-full">
              <label>Dietary Preferences</label>

              <select
                name="diet_pref"
                value={formData.diet_pref}
                onChange={handleChange}
              >
                <option value="">Select Dietary Preference</option>

                {dietPrefOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* STATUS */}

            {isEditMode && (
              <div className="gm-guest-form-group">
                <label>Status</label>

                <div className="gm-guest-status-toggle">
                  <label className="gm-guest-switch">
                    <input
                      type="checkbox"
                      checked={formData.is_active === 1}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,

                          is_active: e.target.checked ? 1 : 0,
                        }))
                      }
                    />

                    <span className="gm-guest-slider" />
                  </label>

                  <span className="gm-guest-status-label">
                    {formData.is_active === 1 ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="gm-guest-form-footer">
            <button
              className="gm-guest-btn gm-guest-btn-secondary"
              onClick={closeForm}
            >
              Cancel
            </button>

            <button
              className="gm-guest-btn gm-guest-btn-primary"
              onClick={handleSubmit}
              disabled={formLoading}
            >
              {formLoading
                ? "Saving..."
                : isEditMode
                  ? "Update Guest"
                  : "Save Guest"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  // GUEST LIST
  // ════════════════════════════════════════════════════

  return (
    <div className="gm-guest-list-page">
      {/* TOP BAR */}

      <div className="gm-guest-page-header">
        <div>
          <h1 className="gm-guest-page-title">Guest Master</h1>

          <p className="gm-guest-page-subtitle">
            Manage guest profiles and information
          </p>
        </div>

        <div className="gm-guest-page-actions">
          {/* SEARCH */}

          <div className="gm-guest-search-box">
            <span className="gm-guest-search-icon">🔍</span>

            <input
              type="text"
              placeholder="Search guests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* EVENT FILTER */}

          <Select
            options={eventOptions}
            value={selectedEventFilter}
            onChange={(selected) => {
              setSelectedEventFilter(selected);

              setSelectedGuestFilter(null);
            }}
            isSearchable
            isClearable
            isLoading={eventLoading}
            placeholder="Select Event"
            classNamePrefix="gm-event-filter"
          />

          {/* GUEST FILTER */}
          {/* 
          <Select
            options={
              selectedEventFilter
                ? guestOptions.filter(
                    (guest) =>
                      String(guest.event_id) ===
                      String(selectedEventFilter.value),
                  )
                : guestOptions
            }
            value={selectedGuestFilter}
            onChange={(selected) => {
              setSelectedGuestFilter(selected);
            }}
            isSearchable
            isClearable
            placeholder="Select Guest"
            classNamePrefix="gm-guest-filter"
          /> */}

          {/* ADD */}

          <button
            className="gm-guest-btn gm-guest-btn-primary"
            onClick={openAdd}
          >
            + Add Guest
          </button>
        </div>
      </div>

      {/* TABLE */}

      <div className="gm-guest-table-card">
        <div className="gm-guest-table-responsive">
          <table className="gm-guest-table">
            <thead>
              <tr>
                <th className="gm-guest-col-id">#</th>

                <th className="gm-guest-col-name">Name</th>

                <th className="gm-guest-col-contact">Contact</th>

                <th className="gm-guest-col-location">Location</th>

                <th className="gm-guest-col-amount">Amount</th>

                <th className="gm-guest-col-diet">Diet</th>

                <th className="gm-guest-col-active">Status</th>

                <th className="gm-guest-col-actions">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="gm-guest-empty-row">
                    Loading guests...
                  </td>
                </tr>
              ) : filteredGuests.length === 0 ? (
                <tr>
                  <td colSpan="8">
                    <div className="gm-guest-empty-state">
                      <div className="gm-guest-empty-icon">👤</div>

                      <div className="gm-guest-empty-title">
                        No Guests Found
                      </div>

                      <div className="gm-guest-empty-desc">
                        {searchTerm
                          ? `No guests match "${searchTerm}"`
                          : "Start by adding your first guest"}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredGuests.map((guest, index) => (
                  <tr key={guest.id}>
                    <td className="gm-guest-col-id">{index + 1}</td>

                    <td className="gm-guest-col-name">
                      <div className="gm-guest-full-name">
                        {guest.title && (
                          <span className="gm-guest-title">{guest.title}</span>
                        )}

                        <span className="gm-guest-first">
                          {guest.first_name}
                        </span>

                        <span className="gm-guest-last">{guest.last_name}</span>
                      </div>

                      <div className="gm-guest-dob">
                        Born: {formatDate(guest.birth_date)}
                      </div>
                    </td>

                    <td className="gm-guest-col-contact">
                      <div className="gm-guest-email">
                        <a
                          href={`mailto:${guest.email}`}
                          className="gm-guest-email-link"
                        >
                          {guest.email}
                        </a>
                      </div>

                      <div className="gm-guest-phone">{guest.phone}</div>
                    </td>

                    <td className="gm-guest-col-location">
                      <div>
                        {guest.city || "-"}
                        {guest.city && guest.state ? ", " : ""}
                        {guest.state || ""}
                      </div>

                      <div className="gm-guest-country">
                        {guest.country || "-"}
                      </div>
                    </td>

                    <td className="gm-guest-col-amount">
                      {formatCurrency(guest.amount, guest.currency)}
                    </td>

                    <td className="gm-guest-col-diet">
                      {guest.diet_pref ? (
                        <span className="gm-guest-diet-badge">
                          {guest.diet_pref}
                        </span>
                      ) : (
                        <span className="gm-guest-diet-none">—</span>
                      )}
                    </td>

                    <td className="gm-guest-col-active">
                      <label className="gm-guest-switch">
                        <input
                          type="checkbox"
                          checked={guest.is_active}
                          onChange={() => toggleGuestStatus(guest)}
                        />

                        <span className="gm-guest-slider" />
                      </label>
                    </td>

                    <td className="gm-guest-col-actions">
                      <div className="gm-guest-action-group">
                        <button
                          className="gm-guest-action-btn gm-guest-action-btn-edit"
                          onClick={() => openEdit(guest)}
                          title="Edit"
                        >
                          <img
                            src={editIcon}
                            alt="Edit"
                            className="gm-guest-action-icon"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
